/**
 * Image Upload component with S3 presigned URL support
 * Uses Pica for high-quality Lanczos resampling (same as Photoshop)
 */
import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Upload, X, Loader2, Image as ImageIcon, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import Pica from "pica";

// Initialize Pica with high-quality settings
const pica = new Pica({
  features: ["js", "wasm", "ww"], // Use WebAssembly and Web Workers for speed
});

// Maximum dimension for resized images
const MAX_IMAGE_DIMENSION = 2000;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

/**
 * Resize an image using Pica (Lanczos resampling - same as Photoshop)
 * @param {File} file - The image file to resize
 * @param {number} maxDimension - Maximum width or height
 * @returns {Promise<Blob>} - Resized image as Blob
 */
async function resizeImage(file, maxDimension = MAX_IMAGE_DIMENSION) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = async () => {
      try {
        let { width, height } = img;
        const originalWidth = width;
        const originalHeight = height;
        
        // Calculate new dimensions maintaining aspect ratio
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }
        
        // If no resize needed, return original
        if (width === originalWidth && height === originalHeight) {
          const blob = await new Promise(res => {
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);
            canvas.toBlob(res, file.type === "image/png" ? "image/png" : "image/jpeg", 0.92);
          });
          resolve(blob);
          return;
        }
        
        // Create source canvas
        const srcCanvas = document.createElement("canvas");
        srcCanvas.width = originalWidth;
        srcCanvas.height = originalHeight;
        const srcCtx = srcCanvas.getContext("2d");
        srcCtx.drawImage(img, 0, 0);
        
        // Create destination canvas
        const destCanvas = document.createElement("canvas");
        destCanvas.width = width;
        destCanvas.height = height;
        
        // Use Pica for high-quality resize (Lanczos3 filter)
        await pica.resize(srcCanvas, destCanvas, {
          quality: 3, // Highest quality (Lanczos3)
          alpha: file.type === "image/png",
          unsharpAmount: 80,    // Light sharpening to counteract softening
          unsharpRadius: 0.6,
          unsharpThreshold: 2
        });
        
        // Convert to blob with high quality
        const blob = await pica.toBlob(
          destCanvas,
          file.type === "image/png" ? "image/png" : "image/jpeg",
          0.92 // High quality JPEG
        );
        
        resolve(blob);
      } catch (err) {
        reject(err);
      } finally {
        URL.revokeObjectURL(img.src);
      }
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error("Failed to load image"));
    };
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Get image dimensions
 */
async function getImageDimensions(file) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => resolve({ width: 0, height: 0 });
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Format file size for display
 */
function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

export function ImageUpload({ 
  currentImage, 
  onImageChange, 
  token,
  label = "Upload Image",
  shape = "circle", // "circle" or "square"
  size = "lg", // "sm", "md", "lg"
  fallbackText = "?",
  className = ""
}) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [autoResize, setAutoResize] = useState(true);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const sizeClasses = {
    sm: "w-16 h-16",
    md: "w-20 h-20",
    lg: "w-24 h-24",
    xl: "w-32 h-32"
  };

  const handleFileSelect = async (file) => {
    if (!file) return;
    
    setError(null);

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file (JPG, PNG, or WebP)");
      toast.error("Please select an image file");
      return;
    }

    // Check if file type is supported (handle iPhone HEIC)
    const supportedTypes = ["image/jpeg", "image/png", "image/webp", "image/jpg", "image/heic", "image/heif"];
    const actualType = file.type || "image/jpeg"; // Some mobile browsers don't set type
    
    // iPhone photos may come as HEIC - they need conversion
    if (actualType === "image/heic" || actualType === "image/heif" || file.name.toLowerCase().endsWith(".heic")) {
      setError("HEIC format not supported. Please use your phone's camera settings to save as JPEG, or export from Photos as JPEG.");
      toast.error("HEIC images not supported - please use JPEG");
      return;
    }

    if (!supportedTypes.includes(actualType) && actualType !== "") {
      setError(`Unsupported format: ${actualType}. Please use JPG, PNG, or WebP.`);
      toast.error("Unsupported image format");
      return;
    }

    // Get image dimensions
    const dimensions = await getImageDimensions(file);
    const isLargeImage = dimensions.width > MAX_IMAGE_DIMENSION || dimensions.height > MAX_IMAGE_DIMENSION;
    
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      if (autoResize) {
        // Will resize, so continue
        toast.info(`Resizing large image (${formatFileSize(file.size)})...`);
      } else {
        setError(`File too large: ${formatFileSize(file.size)}. Maximum is 5MB. Enable auto-resize to compress large images.`);
        toast.error(`Image is ${formatFileSize(file.size)} - max is 5MB`);
        return;
      }
    }

    setUploading(true);

    try {
      let uploadFile = file;
      
      // Resize if needed
      if (autoResize && (isLargeImage || file.size > MAX_FILE_SIZE)) {
        try {
          const resizedBlob = await resizeImage(file, MAX_IMAGE_DIMENSION);
          uploadFile = new File([resizedBlob], file.name, { type: resizedBlob.type });
          
          // Check if resized file is still too large
          if (uploadFile.size > MAX_FILE_SIZE) {
            // Try more aggressive compression
            const moreCompressed = await resizeImage(file, 1500);
            uploadFile = new File([moreCompressed], file.name, { type: moreCompressed.type });
          }
          
          if (uploadFile.size > MAX_FILE_SIZE) {
            throw new Error(`Even after resizing, image is ${formatFileSize(uploadFile.size)}. Please use a smaller image.`);
          }
          
          toast.success(`Resized: ${formatFileSize(file.size)} → ${formatFileSize(uploadFile.size)}`);
        } catch (resizeError) {
          setError(`Resize failed: ${resizeError.message}`);
          toast.error("Failed to resize image");
          setUploading(false);
          return;
        }
      }

      // Get presigned URL from backend
      const presignedResponse = await fetch(`${API}/users/presigned-url`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          filename: uploadFile.name,
          content_type: uploadFile.type
        })
      });

      if (!presignedResponse.ok) {
        const errorData = await presignedResponse.json().catch(() => ({}));
        throw new Error(errorData.detail || `Server error (${presignedResponse.status})`);
      }

      const { upload_url, file_url } = await presignedResponse.json();

      // Upload to S3
      const uploadResponse = await fetch(upload_url, {
        method: "PUT",
        headers: {
          "Content-Type": uploadFile.type
        },
        body: uploadFile
      });

      if (!uploadResponse.ok) {
        const statusText = uploadResponse.statusText || "Unknown error";
        throw new Error(`Upload failed: ${statusText} (${uploadResponse.status})`);
      }

      // Update the image URL
      onImageChange(file_url);
      toast.success("Image uploaded successfully!");

    } catch (error) {
      console.error("Upload error:", error);
      setError(error.message || "Failed to upload image");
      toast.error(error.message || "Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleInputChange = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
    // Reset input to allow selecting the same file again
    e.target.value = "";
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    onImageChange("");
    setError(null);
  };

  return (
    <div className={`${className}`}>
      <div className="flex items-start gap-6">
        {/* Image Preview */}
        <div
          className={`relative ${sizeClasses[size]} ${shape === "circle" ? "rounded-full" : "rounded-lg"} border-2 border-dashed ${dragOver ? "border-white" : "border-white/20"} cursor-pointer overflow-hidden transition-colors group`}
          onClick={handleClick}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          data-testid="image-upload-area"
        >
          {currentImage ? (
            <>
              <img 
                src={currentImage} 
                alt="Uploaded" 
                className="w-full h-full object-cover"
              />
              {/* Remove button */}
              <button
                type="button"
                onClick={handleRemove}
                className="absolute top-1 right-1 p-1 bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3 text-white" />
              </button>
            </>
          ) : (
            <div className="w-full h-full bg-white/[0.05] flex items-center justify-center">
              {uploading ? (
                <Loader2 className="w-8 h-8 text-white/40 animate-spin" />
              ) : (
                <ImageIcon className="w-8 h-8 text-white/40" />
              )}
            </div>
          )}

          {/* Upload overlay */}
          {!uploading && (
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <Upload className="w-6 h-6 text-white" />
            </div>
          )}
        </div>

        {/* Upload button and info */}
        <div className="flex-1">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleInputChange}
            className="hidden"
          />
          
          <Button
            type="button"
            variant="outline"
            onClick={handleClick}
            disabled={uploading}
            className="border-white/20 text-white hover:bg-white/10 rounded-sm"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                {label}
              </>
            )}
          </Button>
          
          <p className="text-white/40 text-sm mt-2">
            JPG, PNG or WebP. Max 5MB.
          </p>
          <p className="text-white/30 text-xs mt-1">
            Recommended: 2000px max on longest side
          </p>
          
          {/* Auto-resize toggle */}
          <div className="flex items-center gap-2 mt-3">
            <Switch
              checked={autoResize}
              onCheckedChange={setAutoResize}
              className="data-[state=checked]:bg-white"
            />
            <span className="text-white/50 text-sm">Auto-resize large images</span>
          </div>
        </div>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-sm flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}
    </div>
  );
}

export default ImageUpload;
